/**
 * Urgent live fix: makes the intl-tel-input country search work for iOS users.
 * Loaded right after intlTelInput.min.js. It wraps window.intlTelInput, so
 * every phone field (login, cart, account addresses) gets the fix.
 *
 * 1. The dropdown shows Arabic names, and the library searched only the shown
 *    name, the dial code and the ISO2 code. The search now also matches
 *    English names from Intl.DisplayNames.
 * 2. iOS transliteration keeps typed letters "composing" until the user taps a
 *    suggestion, and the search did not see them. The search now also uses
 *    the text from `compositionupdate`.
 * 3. An iPhone test showed plain `input` events with value "s" and still an
 *    empty list. The likely cause is an invisible direction mark in the value.
 *    The search now removes invisible format characters (\p{Cf}), and runs on
 *    every `input` itself.
 *
 * Uses internals of the vendored intl-tel-input v23.8.2: p, countryList,
 * searchInput, id, _p3, _x, _p4. If any is missing, nothing is patched.
 *
 * Debug: add ?iti-debug=1 to the URL to see the search input's events.
 * Source of truth: frontend/utils/iti-country-search.js in JODISO_Theme.
 */
(function () {
    var original = window.intlTelInput;
    if (typeof original !== 'function' || typeof Proxy !== 'function') return;

    // \p{M}: accents and Arabic marks. \p{Cf}: invisible format characters,
    // such as the direction marks some iOS keyboards add to typed text.
    function normalize(text) {
        return String(text || '')
            .normalize('NFD')
            .replace(/[\p{M}\p{Cf}]/gu, '')
            .toLowerCase()
            .trim();
    }

    // Shows invisible characters: "s" plus a direction mark is "s [U+200E]".
    function describe(text) {
        return Array.from(text || '')
            .map(function (ch) {
                return /[\p{L}\p{N}\p{P}\p{S}]/u.test(ch) ? ch : '[U+' + ch.codePointAt(0).toString(16).toUpperCase().padStart(4, '0') + ']';
            })
            .join(' ');
    }

    var englishNames = null;
    try {
        englishNames = new Intl.DisplayNames(['en'], { type: 'region' });
    } catch (_) {}

    var englishCache = {};
    function englishName(iso2) {
        if (!englishNames) return '';
        if (!(iso2 in englishCache)) {
            var name = '';
            try {
                name = englishNames.of(iso2.toUpperCase()) || '';
                if (name.toUpperCase() === iso2.toUpperCase()) name = '';
            } catch (_) {}
            englishCache[iso2] = normalize(name);
        }
        return englishCache[iso2];
    }

    function matches(country, query) {
        return (
            normalize(country.name).indexOf(query) !== -1 ||
            englishName(country.iso2).indexOf(query) !== -1 ||
            ('+' + country.dialCode).indexOf(query) !== -1 ||
            country.iso2.indexOf(query) !== -1
        );
    }

    function improveCountrySearch(iti) {
        var input = iti && iti.searchInput;
        if (
            !input ||
            !Array.isArray(iti.p) ||
            !iti.countryList ||
            typeof iti._p3 !== 'function' ||
            typeof iti._x !== 'function' ||
            typeof iti._p4 !== 'function'
        ) {
            return;
        }

        input.setAttribute('autocomplete', 'off');
        input.setAttribute('autocorrect', 'off');
        input.setAttribute('autocapitalize', 'off');
        input.setAttribute('spellcheck', 'false');

        var composing = '';
        var log = /[?&]iti-debug\b/.test(location.search) ? showDebugPanel(input) : null;

        function filter() {
            var value = input.value;
            var queries = [value, composing, value + composing].map(normalize).filter(Boolean);
            var list = iti.countryList;
            list.innerHTML = '';
            var first = true;
            iti.p.forEach(function (country) {
                if (queries.length && !queries.some(function (query) { return matches(country, query); })) return;
                var node = country.nodeById && country.nodeById[iti.id];
                if (!node) return;
                list.appendChild(node);
                if (first) {
                    iti._x(node, false);
                    first = false;
                }
            });
            if (first) iti._x(null, false);
            list.scrollTop = 0;
            iti._p4();
            if (log) log('filter queries=' + JSON.stringify(queries) + ' shown=' + list.childElementCount);
        }

        // The library calls this on open and on each debounced `input`. It asks
        // for the full list when the value is empty, even while letters are
        // composing, so the arguments are ignored and filter() decides.
        iti._p3 = function () {
            filter();
        };

        // Also search on every input ourselves, so it never depends on the
        // library's own listener. It is cheap: about 240 countries.
        input.addEventListener('input', filter);
        input.addEventListener('compositionstart', function () {
            composing = '';
        });
        input.addEventListener('compositionupdate', function (event) {
            composing = event.data || '';
            filter();
        });
        input.addEventListener('compositionend', function () {
            composing = '';
            filter();
        });

    }

    function showDebugPanel(input) {
        var panel = document.getElementById('iti-debug-panel');
        if (!panel) {
            panel = document.createElement('pre');
            panel.id = 'iti-debug-panel';
            panel.style.cssText =
                'position:fixed;left:0;right:0;bottom:0;z-index:2147483647;max-height:40vh;overflow:auto;' +
                'margin:0;padding:6px;background:#000;color:#0f0;font:11px/1.3 monospace;white-space:pre-wrap;direction:ltr;';
            document.body.appendChild(panel);
        }
        ['keydown', 'beforeinput', 'input', 'compositionstart', 'compositionupdate', 'compositionend'].forEach(function (type) {
            input.addEventListener(type, function (event) {
                var line = [
                    type,
                    event.inputType ? 'type=' + event.inputType : '',
                    event.key !== undefined ? 'key=' + JSON.stringify(event.key) : '',
                    event.data !== undefined ? 'data=' + JSON.stringify(event.data) : '',
                    event.isComposing ? 'composing' : '',
                    'value=' + JSON.stringify(input.value),
                    'chars=' + describe(input.value),
                ]
                    .filter(Boolean)
                    .join(' ');
                write(line);
            });
        });
        function write(line) {
            panel.textContent = (line + '\n' + panel.textContent).slice(0, 4000);
        }
        return write;
    }

    // A Proxy keeps every static property (utils, getInstance, ...) on the real function.
    window.intlTelInput = new Proxy(original, {
        apply: function (target, thisArg, args) {
            var iti = Reflect.apply(target, thisArg, args);
            try {
                improveCountrySearch(iti);
            } catch (_) {}
            return iti;
        },
    });
})();
